import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { 
  BarChart, 
  People, 
  Gear, 
  ChatSquare, 
  Activity,
  Database,
  ShieldCheck,
  CloudArrowUpFill,
  ClipboardData
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import config from '../config';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchQuickStats();
  }, []);

  const fetchQuickStats = async () => {
    try {
      setError(null);
      
      const response = await fetch(`${config.api.url}admin/stats?format=json`);
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setError(null);
      } else {
        const errorText = await response.text();
        console.error('Stats API error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(`Failed to load dashboard statistics: ${err.message}`);
      setStats(null);
    }
  };

  const regenerateContent = async () => {
    try {
      setRegenerating(true);
      setMessage(null);
      
      const response = await fetch(`${config.api.url}admin/regenerate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setMessage({
          type: 'success',
          text: `Content regenerated successfully! Generated ${result.pagesGenerated} pages with ${result.totalSections} sections.`
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setMessage({
        type: 'danger',
        text: `Failed to regenerate content: ${error.message}`
      });
    } finally {
      setRegenerating(false);
    }
  };

  const adminSections = [
    {
      title: 'Platform Statistics',
      description: 'View comprehensive platform analytics and metrics',
      icon: <BarChart size={24} />,
      link: '/admin/stats',
      color: 'danger', // Changed from 'primary' to 'danger' for red color
      stats: stats ? [
        { label: 'Total Users', value: stats.users?.total || 0 },
        { label: 'New This Week', value: stats.users?.recent_signups || 0 }
      ] : []
    },
    {
      title: 'User Management',
      description: 'Manage user accounts, roles, and permissions',
      icon: <People size={24} />,
      link: '/admin/users',
      color: 'success',
      stats: stats ? [
        { label: 'Confirmed Users', value: stats.users?.confirmed || 0 },
        { label: 'New This Week', value: stats.users?.recent_signups || 0 }
      ] : []
    },
    {
      title: 'Content Management',
      description: 'Edit website content, pages, and announcements',
      icon: <Database size={24} />,
      link: '/admin/content',
      color: 'dark',
      stats: stats ? [
        { label: 'Content Pages', value: stats.content?.total_pages || 0 },
        { label: 'Total Sections', value: stats.content?.total_sections || 0 }
      ] : [
        { label: 'Pages', value: 'Multiple' },
        { label: 'Last Edit', value: 'Recent' }
      ]
    },
    {
      title: 'Challenge Configuration',
      description: 'Configure tracks, deadlines, and grading criteria',
      icon: <Gear size={24} />,
      link: '/admin/tracks',
      color: 'warning',
      stats: [
        { label: 'Active Tracks', value: 3 },
        { label: 'Last Updated', value: 'Today' }
      ]
    },
    {
      title: 'Questions & Answers',
      description: 'Manage participant questions and provide answers',
      icon: <ChatSquare size={24} />,
      link: '/admin/questions',
      color: 'secondary',
      stats: stats ? [
        { label: 'Total Questions', value: stats.questions?.total || 0 },
        { label: 'Recent Questions', value: stats.questions?.recent || 0 }
      ] : []
    },
    {
      title: 'Submissions & Grading',
      description: 'Manage submission reviews and view scoring summaries',
      icon: <ClipboardData size={24} />,
      link: '/review',
      color: 'info',
      stats: stats ? [
        { label: 'Total Submissions', value: stats.submissions?.total || 0 },
        { label: 'Completed Reviews', value: stats.reviews?.total || 0 }
      ] : [],
      buttons: [
        { text: 'Assign Reviewers', link: '/admin/assign-reviews', variant: 'info' },
        { text: 'Grading Results', link: '/review/summary', variant: 'info' }
      ]
    }
  ];

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center mb-3">
            <ShieldCheck size={32} className="me-3 text-primary" />
            <div>
              <h1 className="mb-0">Admin Dashboard</h1>
              <p className="text-muted mb-0">Quantum City Challenge Administration</p>
            </div>
          </div>
          
          {error && (
            <Alert variant="warning" className="mb-3">
              <Activity className="me-2" />
              {error}
            </Alert>
          )}
          
          {message && (
            <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}
          
          <Alert variant="info" className="mb-4">
            <Activity className="me-2" />
            Welcome to the admin dashboard. Use the sections below to manage different aspects of the platform.
          </Alert>
        </Col>
      </Row>

      {/* Content Regeneration Section */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 bg-light">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1">
                    <CloudArrowUpFill className="me-2 text-primary" />
                    Content Optimization
                  </h5>
                  <p className="text-muted mb-0">
                    Regenerate static content files for improved performance and CloudFront optimization.
                  </p>
                </div>
                <Button 
                  variant="outline-primary" 
                  onClick={regenerateContent}
                  disabled={regenerating}
                >
                  {regenerating ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <CloudArrowUpFill className="me-2" />
                      Regenerate Content
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {adminSections.map((section, index) => (
          <Col lg={6} xl={4} className="mb-4" key={index}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center mb-3">
                  <div className={`p-2 rounded bg-${section.color} bg-opacity-10 text-${section.color} me-3`}>
                    {section.icon}
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="mb-0">{section.title}</h5>
                  </div>
                </div>
                
                <p className="text-muted mb-3 flex-grow-1">
                  {section.description}
                </p>
                
                {section.stats.length > 0 && (
                  <div className="mb-3">
                    <Row className="g-2">
                      {section.stats.map((stat, statIndex) => (
                        <Col xs={6} key={statIndex}>
                          <div className="text-center p-2 bg-light rounded">
                            <div className="fw-bold text-primary">{stat.value}</div>
                            <small className="text-muted">{stat.label}</small>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
                
                {/* Custom buttons for Submissions & Grading */}
                {section.buttons ? (
                  <div className="mt-auto">
                    <div className="d-flex gap-2">
                      {section.buttons.map((button, buttonIndex) => (
                        <Button 
                          key={buttonIndex}
                          as={Link} 
                          to={button.link} 
                          variant={button.variant}
                          className="flex-fill"
                          style={{color:'white'}}
                        >
                          {button.text}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Button 
                    as={Link} 
                    to={section.link} 
                    variant={section.color}
                    className="mt-auto"
                  >
                    Access {section.title}
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="mt-4">
        <Col>
          <Card className="border-0">
            <Card.Body>
              <h6 className="text-muted mb-2">System Information</h6>
              <small className="text-muted">
                Platform Status: <Badge bg="success">Online</Badge> | 
                Last Updated: {new Date().toLocaleDateString()} | 
                Version: 1.0.0
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminDashboard;
